import React from "react";
import { AlertTriangle, Info, Activity } from "lucide-react";
import { timeAgo } from "@/lib/timeAgo";

// Durée courte lisible : « 45 s », « 3 min », « 1 h 12 ».
const fmtDur = (iso, t) => {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} ${t("time.min", "min")}`;
  return `${Math.floor(s / 3600)} ${t("time.hour", "h")} ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}`;
};

const agoLabel = (iso, t) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  return diff < 60 ? t("time.now", "à l'instant") : t("studio.ago", "il y a {d}").replace("{d}", timeAgo(iso, t));
};

const fill = (s, vars) => Object.keys(vars).reduce((acc, k) => acc.replace(`{${k}}`, vars[k]), s);

// Alerte RÉELLE (coupures OBS → Cloudflare) : encadré ambre. `live` = version courte pour le bandeau du direct.
export const ObsUnstableBox = ({ ingest, t, live = false, testId }) => {
  if (!ingest?.unstable) return null;
  const vars = { n: ingest.drops_5min, ago: agoLabel(ingest.last_drop_at, t) };
  if (live) {
    return (
      <div className="absolute inset-x-4 bottom-4 z-20 flex items-start gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: "rgba(26,18,6,0.92)", border: "1px solid rgba(224,166,60,0.55)", color: "#F0C976" }} data-testid={testId} data-drops={ingest.drops_5min}>
        <AlertTriangle size={15} className="mt-0.5 shrink-0" />
        <span><b>{t("studio.obs_unstable_title", "Connexion OBS instable")}</b> — {fill(t("studio.obs_unstable_live", "OBS s'est déconnecté {n} fois en 5 min — tes spectateurs ont pu voir des coupures. Vérifie la pastille et les images perdues dans OBS."), vars)}</span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-sm px-3 py-2 rounded-md" style={{ color: "#F0C976", background: "rgba(224,166,60,0.14)", border: "1px solid rgba(224,166,60,0.45)" }} aria-live="polite" data-testid={testId} data-drops={ingest.drops_5min}>
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold">{t("studio.obs_unstable_title", "Connexion OBS instable")}</p>
        <p className="text-xs mt-0.5" style={{ color: "#d8c9a0" }}>
          {fill(t("studio.obs_unstable_body", "OBS s'est déconnecté {n} fois en 5 min (dernière coupure : {ago}). Si la pastille OBS est jaune/rouge ou que les images perdues montent : baisse le bitrate à ~2000-2500 kbps, passe en 720p/30 fps et privilégie le câble Ethernet au Wi-Fi."), vars)}
        </p>
      </div>
    </div>
  );
};

// Sous « Flux reçu ✓ » : état factuel de la liaison, rappel « regarde OBS », info neutre si l'aperçu charge.
export const ObsIngestHealth = ({ ingest, previewBuffering, t }) => {
  let line = null;
  if (ingest?.state === "reconnecting") line = t("studio.ingest_reconnecting", "OBS se reconnecte à Cloudflare…");
  else if (ingest?.state === "disconnected") line = t("studio.ingest_disconnected", "OBS n'envoie plus de flux à Cloudflare.");
  else if (ingest?.connected_since) line = fill(t("studio.ingest_stable", "Liaison OBS → Cloudflare stable · connectée depuis {dur}"), { dur: fmtDur(ingest.connected_since, t) });
  return (
    <>
      {ingest && (
        <div className="text-[11px] space-y-0.5" style={{ color: "#A0B0AA" }} data-testid="obs-ingest-health" data-state={ingest.state} data-drops={ingest.drops_5min}>
          {line && <p className="flex items-center gap-1.5"><Activity size={12} className="shrink-0" /> <span>{line}</span></p>}
          <p className="pl-[18px]" data-testid="obs-ingest-drops">{fill(t("studio.ingest_drops", "{n} coupure(s) sur les 5 dernières minutes"), { n: ingest.drops_5min })}</p>
        </div>
      )}
      <ObsUnstableBox ingest={ingest} t={t} testId="obs-network-warning" />
      <p className="text-[11px]" style={{ color: "#6b7d76" }} data-testid="obs-uplink-hint">
        {t("studio.obs_uplink_hint", "Ton débit montant n'est mesurable que dans OBS : surveille la pastille (vert/jaune/rouge) et « Images perdues (réseau) » en bas de la fenêtre.")}
      </p>
      {previewBuffering && (
        <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-md" style={{ color: "#A0B0AA", background: "rgba(160,176,170,0.08)", border: "1px solid rgba(160,176,170,0.25)" }} data-testid="obs-preview-buffering">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>{t("studio.preview_buffering", "Ton aperçu charge par moments : c'est la réception sur cet appareil (navigateur), pas forcément ce que voient tes spectateurs.")}</span>
        </div>
      )}
    </>
  );
};
